/**
 * Feature E02-F01 — "Список проектов (Dashboard)" pure model (T04 stats / filter /
 * search + T06 create-reply reading). Framework-free, DOM-free decision logic so
 * the React view (`DashboardView.tsx`) and the unit tests share one set of rules
 * and no business logic lives in the JSX.
 *
 * 1:1 port of the legacy `../EER app/public/assets/js/dashboard.js`
 * (`renderProjectList` / the three stat cards / `_dashboardFilter` toggle /
 * the search box filter / `createManualProject` reply reading):
 *
 *   - the three stat buckets are counted CLIENT-SIDE off `stage`
 *     (kickoff = stage===1, inprogress = stage 2..3, launched = every stage the
 *     row badge labels "Launched", i.e. 4 & 5 — E12-F17-T13: the counter and the
 *     badge share `STAGE_LABELS`, so LAUNCHED always equals the visible
 *     Launched rows; the legacy `stage===5 || status==='launched'` rule missed
 *     stage-4 projects and showed LAUNCHED 0);
 *   - clicking a stat card TOGGLES its filter (re-clicking the active card clears
 *     it) — `toggleFilter`;
 *   - the search box narrows by a case-insensitive substring over the role name
 *     AND the client name (the two visible columns);
 *   - the stage badge label/class come from the shared `STAGE_LABELS` /
 *     `STAGE_BADGE_CLASSES` (1 Pre-Kickoff/orange … 4&5 Launched/emerald);
 *   - the owner avatar = the first two characters of the owner id, uppercased
 *     ('Unknown' when the owner is null).
 */
import {
  DASHBOARD_FILTERS,
  STAGE_BADGE_CLASSES,
  STAGE_LABELS,
  type DashboardFilter,
  type ProjectListItemDTO,
} from '@eer/shared';

export { DASHBOARD_FILTERS, STAGE_LABELS, STAGE_BADGE_CLASSES };
export type { DashboardFilter };

// ---------------------------------------------------------------------------
// T04 — stat buckets (the three cards)
// ---------------------------------------------------------------------------

/** Per-bucket count for the three stat cards. */
export interface DashboardStats {
  /** stage === 1 (Awaiting Kick-off). */
  readonly kickoff: number;
  /** stage 2..3 (In Progress — Kick-off / Generation badges). */
  readonly inprogress: number;
  /** stage whose badge reads "Launched" (4 & 5). */
  readonly launched: number;
}

/**
 * True when the row's stage badge reads "Launched" (`STAGE_LABELS` 4 & 5).
 * Single source for the LAUNCHED counter/filter so it can never diverge from
 * the badge the table renders (E12-F17-T13).
 */
export function isLaunchedStage(stage: number | null | undefined): boolean {
  return STAGE_LABELS[stage ?? 0] === 'Launched';
}

/** True when a project belongs to a given dashboard bucket (badge-aligned). */
export function matchesFilter(
  project: Pick<ProjectListItemDTO, 'stage' | 'status'>,
  filter: DashboardFilter,
): boolean {
  const stage = project.stage ?? 0;
  switch (filter) {
    case 'kickoff':
      return stage === 1;
    case 'inprogress':
      return stage >= 2 && stage <= 3;
    case 'launched':
      return isLaunchedStage(stage);
    default:
      return false;
  }
}

/** Count the three stat buckets over the full (unfiltered) portfolio. */
export function computeStats(
  projects: readonly Pick<ProjectListItemDTO, 'stage' | 'status'>[],
): DashboardStats {
  // Defensive: never iterate a non-array. A malformed response (e.g. a legacy
  // envelope collapsing a 1-row list into a bare object) must degrade to zero
  // stats, NOT crash `for…of` ("not iterable") and white-screen the whole authed
  // shell. The transport-level fix lives in api/client.ts; this is the cheap
  // belt-and-braces guard so a future bad shape can never blank the app.
  const list = Array.isArray(projects) ? projects : [];
  let kickoff = 0;
  let inprogress = 0;
  let launched = 0;
  for (const p of list) {
    if (matchesFilter(p, 'kickoff')) kickoff += 1;
    if (matchesFilter(p, 'inprogress')) inprogress += 1;
    if (matchesFilter(p, 'launched')) launched += 1;
  }
  return { kickoff, inprogress, launched };
}

// ---------------------------------------------------------------------------
// T04 — filter toggle (clicking a card toggles its bucket)
// ---------------------------------------------------------------------------

/**
 * Toggle the active dashboard filter (legacy `_dashboardFilter` click handler):
 * clicking the already-active card clears the filter (returns null), any other
 * card selects it.
 */
export function toggleFilter(
  current: DashboardFilter | null,
  clicked: DashboardFilter,
): DashboardFilter | null {
  return current === clicked ? null : clicked;
}

// ---------------------------------------------------------------------------
// T04 — search (substring over role + client + owner)
// ---------------------------------------------------------------------------

/**
 * True when a project matches the search query (case-insensitive substring).
 *
 * Legacy parity (`dashboard.js → renderProjectList`): the search haystack joined
 * `client, client_name, role_name, owner_id, contact_name`. The v2
 * `ProjectListItemDTO` carries `role_name`, `client_name` and `owner_id` (there
 * is no `contact_name` on the list projection), so we match over those three —
 * restoring the previously-dropped owner column to the search scope.
 *
 * Retained as a reusable model helper; the T10 redesign replaced the single
 * merged search box with dedicated Client/Position facets (see `matchesClient` /
 * `matchesPosition`), so the live pipeline no longer wires this in.
 */
export function matchesSearch(
  project: Pick<ProjectListItemDTO, 'role_name' | 'client_name' | 'owner_id'>,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  const haystack = [project.role_name, project.client_name, project.owner_id]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

// ---------------------------------------------------------------------------
// T08 — scope pill (All / My Active)
// ---------------------------------------------------------------------------

/**
 * Dashboard scope pills. `all` shows the whole (role-scoped) portfolio;
 * `mine-active` narrows to the current user's OWN, still-active projects
 * (`owner_id === currentUser.id && status === 'active'`).
 */
export const DASHBOARD_SCOPES = ['all', 'mine-active'] as const;
export type DashboardScope = (typeof DASHBOARD_SCOPES)[number];

/**
 * True when a project is in the selected scope (T08). `all` always matches;
 * `mine-active` requires the project be OWNED by the current user AND `active`.
 * A null/absent current user id can never own a project, so `mine-active` yields
 * nothing (defensive — the authed shell always threads a real id).
 */
export function matchesScope(
  project: Pick<ProjectListItemDTO, 'owner_id' | 'status'>,
  scope: DashboardScope,
  currentUserId: string | null,
): boolean {
  if (scope === 'all') return true;
  if (!currentUserId) return false;
  const status = (project.status ?? '').toLowerCase();
  return project.owner_id === currentUserId && status === 'active';
}

// ---------------------------------------------------------------------------
// T09 — created_at date-range filter
// ---------------------------------------------------------------------------

/**
 * Normalise any parseable date (an ISO `created_at`, or a `<input type="date">`
 * `yyyy-mm-dd` bound) to its UTC calendar day (`yyyy-mm-dd`). Returns null for a
 * blank/unparseable value. Using the UTC day keeps the comparison deterministic
 * regardless of the runtime timezone (unlike `toLocaleDateString`), and `yyyy-mm-dd`
 * strings compare correctly with `<`/`>` (ISO 8601 is lexicographically ordered).
 */
function toUtcDay(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim();
  if (raw.length === 0) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/**
 * True when a project's `created_at` falls within the inclusive `[from, to]`
 * calendar-day range (T09). `from`/`to` are `yyyy-mm-dd` bounds from the date
 * inputs; either may be null (open-ended). No bounds → always matches. An
 * unparseable `created_at` is excluded once any bound is active.
 */
export function matchesCreatedRange(
  project: Pick<ProjectListItemDTO, 'created_at'>,
  from: string | null,
  to: string | null,
): boolean {
  const fromDay = toUtcDay(from);
  const toDay = toUtcDay(to);
  if (!fromDay && !toDay) return true;
  const day = toUtcDay(project.created_at);
  if (!day) return false;
  if (fromDay && day < fromDay) return false;
  if (toDay && day > toDay) return false;
  return true;
}

// ---------------------------------------------------------------------------
// T10 — Client + Position facet filters
// ---------------------------------------------------------------------------

/**
 * True when a project matches the Client facet (T10). When a concrete client was
 * PICKED from the autocomplete (`clientId` is a number) we match precisely by
 * `client_id`; otherwise we fall back to a case-insensitive substring over the
 * typed `client_name` (free text that didn't resolve to a cached client). Blank
 * typed name + null id → inactive (matches all).
 */
export function matchesClient(
  project: Pick<ProjectListItemDTO, 'client_name' | 'client_id'>,
  clientName: string,
  clientId: number | null,
): boolean {
  if (clientId != null) return project.client_id === clientId;
  const q = clientName.trim().toLowerCase();
  if (q.length === 0) return true;
  return (project.client_name ?? '').toLowerCase().includes(q);
}

/**
 * True when a project matches the Position facet (T10): a case-insensitive
 * substring over `role_name`. Blank query → inactive (matches all).
 */
export function matchesPosition(
  project: Pick<ProjectListItemDTO, 'role_name'>,
  position: string,
): boolean {
  const q = position.trim().toLowerCase();
  if (q.length === 0) return true;
  return (project.role_name ?? '').toLowerCase().includes(q);
}

/**
 * Distinct, non-blank `role_name` values across the loaded portfolio, sorted
 * case-insensitively — the client-side source for the Position autocomplete
 * (there is no distinct-role-titles endpoint). De-duplicated case-insensitively
 * while preserving the first-seen original casing.
 */
export function distinctRoleNames(
  projects: readonly Pick<ProjectListItemDTO, 'role_name'>[],
): string[] {
  if (!Array.isArray(projects)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of projects) {
    const name = (p.role_name ?? '').trim();
    if (name.length === 0) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out.sort((a, b) => a.localeCompare(b));
}

// ---------------------------------------------------------------------------
// Combined filter pipeline (stat-card bucket + scope + date range + facets)
// ---------------------------------------------------------------------------

/** The full set of active dashboard filters (built by the view from its state). */
export interface DashboardCriteria {
  /** Active stat-card bucket (kickoff/inprogress/launched) or null. */
  readonly filter: DashboardFilter | null;
  /** Scope pill (T08). */
  readonly scope: DashboardScope;
  /** Current user id — required for the `mine-active` scope (T08). */
  readonly currentUserId: string | null;
  /** Inclusive created_at lower bound (`yyyy-mm-dd`) or null (T09). */
  readonly createdFrom: string | null;
  /** Inclusive created_at upper bound (`yyyy-mm-dd`) or null (T09). */
  readonly createdTo: string | null;
  /** Typed client name for the Client facet (T10). */
  readonly clientName: string;
  /** Picked client id (exact match) or null for substring on the name (T10). */
  readonly clientId: number | null;
  /** Typed position (role_name) for the Position facet (T10). */
  readonly position: string;
}

/**
 * Apply the stat-card bucket, the scope pill, the created_at range and the
 * Client/Position facets to the portfolio (all AND-combined). Filter/scope
 * first (cheapest, most selective), then the range and the two facets.
 */
export function applyDashboardFilters(
  projects: readonly ProjectListItemDTO[],
  criteria: DashboardCriteria,
): ProjectListItemDTO[] {
  // Defensive (see `computeStats`): a non-array shape degrades to an empty list
  // instead of throwing on `.filter`.
  if (!Array.isArray(projects)) return [];
  const { filter, scope, currentUserId, createdFrom, createdTo, clientName, clientId, position } =
    criteria;
  return projects.filter(
    (p) =>
      (filter === null || matchesFilter(p, filter)) &&
      matchesScope(p, scope, currentUserId) &&
      matchesCreatedRange(p, createdFrom, createdTo) &&
      matchesClient(p, clientName, clientId) &&
      matchesPosition(p, position),
  );
}

// ---------------------------------------------------------------------------
// T04 — row projection (stage badge / owner avatar / updated date)
// ---------------------------------------------------------------------------

/** The display badge for a project's stage (label + Tailwind classes). */
export interface StageBadge {
  readonly label: string;
  readonly className: string;
}

/** Project the stage badge (legacy `renderProjectList` switch). Unknown → '—'. */
export function stageBadge(stage: number | null | undefined): StageBadge {
  const s = stage ?? 0;
  return {
    label: STAGE_LABELS[s] ?? '—',
    className: STAGE_BADGE_CLASSES[s] ?? 'bg-gray-100 text-gray-600',
  };
}

/** The owner display name ('Unknown' when null, legacy fallback). */
export function ownerLabel(ownerId: string | null | undefined): string {
  const raw = (ownerId ?? '').trim();
  return raw.length > 0 ? raw : 'Unknown';
}

/**
 * Avatar initials = the first two characters of the owner id, uppercased
 * (legacy `owner.substring(0,2).toUpperCase()`). Null owner → 'UN' (Unknown).
 */
export function ownerInitials(ownerId: string | null | undefined): string {
  return ownerLabel(ownerId).substring(0, 2).toUpperCase();
}

/** The role display name with the legacy empty-state placeholder. */
export function roleLabel(roleName: string | null | undefined): string {
  return (roleName ?? '').trim() || 'New Position';
}

/** The client display name with the legacy empty-state placeholder. */
export function clientLabel(clientName: string | null | undefined): string {
  return (clientName ?? '').trim() || '—';
}

/** Format the Updated column (legacy `new Date(updated_at).toLocaleDateString()`). */
export function formatUpdated(updatedAt: string | null | undefined): string {
  const raw = (updatedAt ?? '').trim();
  if (raw.length === 0) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

// ---------------------------------------------------------------------------
// T06 — create_manual reply reading (the "New Project" button)
// ---------------------------------------------------------------------------

/** The outcome of a create_manual reply (legacy `createManualProject` branch). */
export interface CreateReplyOutcome {
  /** True when the reply carries a numeric `project_id` (success signal). */
  readonly ok: boolean;
  /** The new project id (present only when ok). */
  readonly projectId?: number;
  /** Error text to toast when not ok (`data.error || 'Error creating project'`). */
  readonly error?: string;
}

/** Default error copy when the create reply carries no `error` (legacy parity). */
export const CREATE_ERROR_FALLBACK = 'Error creating project';

/**
 * Read a create_manual reply and determine success from the data field, not from
 * the `success` flag. The API client strips `success:true` from object responses
 * (`unwrapLegacySuccess` in packages/shared/src/legacy.ts), so `reply.success`
 * is always `undefined` after the strip. A numeric `project_id` is the reliable
 * success signal: the create route returns `{ project_id: N }` on success and
 * `{ success:false, error }` on failure (the `success:false` body is NOT stripped
 * and carries no `project_id`, so it still routes to the error branch with its
 * message).
 */
export function readCreateReply(reply: {
  success?: boolean;
  project_id?: number;
  error?: string;
}): CreateReplyOutcome {
  if (typeof reply.project_id === 'number') {
    return { ok: true, projectId: reply.project_id };
  }
  return { ok: false, error: reply.error || CREATE_ERROR_FALLBACK };
}
